import type { FEDispatchSelection } from '@/components/forms/parentTicket/FEDispatchFlow'
import { childTicketService } from '@/lib/services/childTicketService'
import type { FESchedule } from '@/types/parentTicket'
import { scheduleChildTicketId } from '@/components/forms/parentTicket/feDispatchUtils'

export type ReassignEngineerSchedulePayload = {
  schedule_date?: string
  schedule_end_date?: string | null
  schedule_time?: string
  schedule_end_time?: string
  latitude?: number | null
  longitude?: number | null
  location_address?: string | null
  contact_number_override?: string | null
}

export function buildReassignmentSchedulePayload(
  selection: FEDispatchSelection,
): ReassignEngineerSchedulePayload {
  return {
    schedule_date: selection.scheduleDate,
    schedule_end_date: selection.spansMultipleDays ? (selection.scheduleEndDate ?? null) : null,
    schedule_time: selection.scheduleTime,
    schedule_end_time: selection.scheduleEndTime,
    latitude: selection.latitude,
    longitude: selection.longitude,
    location_address: selection.storeAddress || null,
    contact_number_override: selection.contactNumberOverride ?? null,
  }
}

export function resolveLinkedChildTicketId(
  selection: FEDispatchSelection,
  scheduleId: number | null,
  schedules: FESchedule[],
): { childTicketId: number | null; ambiguous: boolean } {
  const fromSelection = selection.childTicketId ?? selection.child_ticket_id
  if (fromSelection != null && Number(fromSelection) > 0) {
    return { childTicketId: Number(fromSelection), ambiguous: false }
  }

  const matches = schedules.filter((s) => {
    if (scheduleId != null && s.id === scheduleId) return true
    return Number(s.fe_user_id) === Number(selection.feUserId)
  })

  const childIds = [
    ...new Set(
      matches
        .map((s) => scheduleChildTicketId(s))
        .filter((id): id is number => id != null && Number(id) > 0),
    ),
  ]

  if (childIds.length === 1) {
    return { childTicketId: childIds[0], ambiguous: false }
  }
  if (childIds.length > 1) {
    return { childTicketId: null, ambiguous: true }
  }

  return { childTicketId: null, ambiguous: false }
}

export type ReassignmentResult = {
  old_task: Awaited<ReturnType<typeof childTicketService.reassignEngineer>>['old_task']
  new_task: Awaited<ReturnType<typeof childTicketService.reassignEngineer>>['new_task']
  new_schedule: Awaited<ReturnType<typeof childTicketService.reassignEngineer>>['new_schedule']
}

export async function confirmEngineerReassignment(input: {
  ticketId: string
  taskId: number
  replacementFeUserId: number
  reason?: string
  selection: FEDispatchSelection
}): Promise<ReassignmentResult> {
  return childTicketService.reassignEngineer(input.ticketId, input.taskId, {
    fe_user_id: input.replacementFeUserId,
    reason: input.reason,
    ...buildReassignmentSchedulePayload(input.selection),
  })
}
